import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useObtener, useHoras, usePrimerAnioHoras } from './DatosContext';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');


// ─────────────────────────────────────────────
// PALETA METRON
// ─────────────────────────────────────────────
const C = {
  indigo:   '#534AB7',
  violet:   '#7B6FE8',
  mist:     '#AFA9EC',
  positive: '#1D9E75',
  negative: '#D85A30',
  warning:  '#BA7517',
  night:    '#1a1440',
  bg:       '#0e0b24',
  surface:  '#1a1440',
  surface2: '#231b54',
  border:   'rgba(83,74,183,0.25)',
  text:     '#ffffff',
  muted:    'rgba(255,255,255,0.55)',
};

const SECTOR_COLORS = [
  '#534AB7','#7B6FE8','#1D9E75','#D85A30','#BA7517',
  '#3B82F6','#EC4899','#10B981','#F59E0B','#6366F1',
];

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtHoras = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtEur = (n) => `${fmt(n)} €`;
const fmtEur2 = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' €';
const fmt1 = (n) => new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0);
const fmtPct = (n) => `${fmt1(n)}%`;
const pct = (a, b) => fmt1(b > 0 ? (a / b) * 100 : 0);

const toDate = (fecha) => {
  if (!fecha) return null;
  if (fecha?.toDate) return fecha.toDate();
  if (fecha?.seconds) return new Date(fecha.seconds * 1000);
  const d = new Date(fecha);
  return isNaN(d) ? null : d;
};

const getYear = (fecha) => { const d = toDate(fecha); return d ? d.getFullYear() : null; };
const getMonth = (fecha) => { const d = toDate(fecha); return d !== null ? d.getMonth() : null; };

// Año de un proyecto en el CRM: su fecha_inicio. Los proyectos antiguos (2009-2016) no la tienen:
// para ellos se usa el año de su código, cuyas primeras cifras son el año (9001 = 2009, 16037 = 2016).
// Solo da el año: en la estacionalidad mensual esos proyectos no aparecen (sesión 31).
const añoDeCodigo = (codigo) => {
  const t = String(codigo ?? '').trim();
  if (!/^\d{4,5}$/.test(t)) return null;
  const y = 2000 + Number(t.length === 4 ? t.slice(0, 1) : t.slice(0, 2));
  return y >= 2000 && y <= new Date().getFullYear() ? y : null;
};
const getYearProyecto = (p) => getYear(p.fecha_inicio) || añoDeCodigo(p.codigo);

const diffDias = (fechaA, fechaB) => {
  const a = toDate(fechaA), b = toDate(fechaB);
  if (!a || !b) return null;
  return Math.round(Math.abs(b - a) / (1000 * 60 * 60 * 24));
};

const colorMargen = (m) => {
  if (m === null || m === undefined || isNaN(m)) return C.muted;
  if (m >= 50) return C.positive;
  if (m >= 35) return C.warning;
  return C.negative;
};

// ─────────────────────────────────────────────
// COMPONENTES REUTILIZABLES
// ─────────────────────────────────────────────
const KPICard = ({ label, value, sub, color, small }) => (
  <div style={{
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: small ? '14px 18px' : '18px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    flex: 1,
    minWidth: 160,
  }}>
    <span style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
    <span style={{ fontSize: small ? 20 : 26, fontWeight: 700, color: color || C.text, lineHeight: 1.1 }}>{value}</span>
    {sub && <span style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{sub}</span>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: 13, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '28px 0 14px' }}>
    {children}
  </h3>
);

const ChartCard = ({ title, children, height = 280 }) => (
  <div style={{
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: '20px 20px 12px',
  }}>
    {title && <div style={{ fontSize: 13, fontWeight: 600, color: C.muted, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</div>}
    <div style={{ width: '100%', height, minHeight: height }}>
      {children}
    </div>
  </div>
);

const SinDatos = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.muted, fontSize: 13 }}>
    Sin datos con los filtros actuales
  </div>
);

// Tooltip elegante reutilizable
const TooltipCustom = ({ active, payload, label, formatValue }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(14,11,36,0.97)',
      border: `1px solid ${C.border}`,
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 13,
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    }}>
      {label && <div style={{ color: C.text, marginBottom: 8, fontWeight: 600, fontSize: 12 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: i < payload.length - 1 ? 4 : 0 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color || p.fill, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ color: C.muted, fontSize: 12 }}>{p.name}:</span>
          <span style={{ fontWeight: 700, color: C.text }}>{formatValue ? formatValue(p.value) : fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// Pill de año seleccionable
const YearPill = ({ year, active, onClick }) => (
  <button onClick={() => onClick(year)} style={{
    padding: '5px 14px',
    borderRadius: 20,
    border: `1px solid ${active ? C.indigo : C.border}`,
    background: active ? C.indigo : 'transparent',
    color: active ? '#fff' : C.muted,
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
  }}>
    {year}
  </button>
);

// Toggle Nº / Vol
const ToggleVista = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: `1px solid ${C.border}` }}>
    {['num', 'vol'].map(v => (
      <button key={v} onClick={() => onChange(v)} style={{
        padding: '3px 10px', fontSize: 11, cursor: 'pointer', border: 'none',
        background: value === v ? C.indigo : 'transparent',
        color: value === v ? '#fff' : C.muted,
        fontWeight: value === v ? 600 : 400,
        transition: 'all 0.15s',
      }}>
        {v === 'num' ? 'Nº' : 'Vol. €'}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────
// DROPDOWN SECTORES — reutilizable en ambas pestañas
// Set vacío + ninguno=false → todos activos
// Set con elementos + ninguno=false → solo esos activos  
// ninguno=true → ninguno activo (filtro vacío)
// ─────────────────────────────────────────────
const DropdownSectores = ({ sectoresDisponibles, sectoresActivos, setSectoresActivos }) => {
  const [abierto, setAbierto] = useState(false);

  // Tres estados posibles:
  // 'todos'  → sectoresActivos = new Set()  (sin filtro, muestra todo)
  // 'algunos'→ sectoresActivos = Set con elementos
  // 'ninguno'→ sectoresActivos = Set con valor especial '__ninguno__'
  const esNinguno = sectoresActivos.has('__ninguno__');
  const esTodos = !esNinguno && sectoresActivos.size === 0;
  const esAlgunos = !esNinguno && sectoresActivos.size > 0;

  const label = esTodos
    ? 'Todos los sectores'
    : esNinguno
      ? 'Ningún sector'
      : sectoresActivos.size === 1
        ? [...sectoresActivos][0]
        : `${sectoresActivos.size} sectores`;

  useEffect(() => {
    if (!abierto) return;
    const handler = (e) => { if (!e.target.closest('[data-dropdown-sectores]')) setAbierto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [abierto]);

  const isMarcado = (s) => esTodos || (esAlgunos && sectoresActivos.has(s));

  const toggleS = (s) => {
    setSectoresActivos(prev => {
      if (esNinguno) {
        // Desde ninguno: marcar uno activa solo ese
        return new Set([s]);
      }
      if (esTodos) {
        // Desde todos: desmarcar uno deja todos EXCEPTO ese
        return new Set(sectoresDisponibles.filter(x => x !== s));
      }
      // Desde algunos: toggle normal
      const next = new Set(prev);
      if (next.has(s)) {
        next.delete(s);
        if (next.size === 0) return new Set(); // último desmarcado → vuelve a todos
      } else {
        next.add(s);
        if (next.size === sectoresDisponibles.length) return new Set(); // todos marcados → reset a todos
      }
      return next;
    });
  };

  if (sectoresDisponibles.length === 0) return null;

  const hayFiltroActivo = !esTodos;

  return (
    <div style={{ position: 'relative' }} data-dropdown-sectores="1">
      <button onClick={() => setAbierto(v => !v)} style={{
        padding: '5px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
        border: `1px solid ${hayFiltroActivo ? C.indigo : C.border}`,
        background: hayFiltroActivo ? 'rgba(83,74,183,0.15)' : 'transparent',
        color: hayFiltroActivo ? C.text : C.muted,
        display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
      }}>
        {label} <span style={{ fontSize: 10, opacity: 0.6 }}>{abierto ? '▲' : '▼'}</span>
      </button>

      {abierto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, marginTop: 4,
          background: C.night, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '6px 0', minWidth: 210, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', gap: 10, padding: '4px 12px 8px', borderBottom: `1px solid ${C.border}` }}>
            <button
              onClick={() => setSectoresActivos(new Set())}
              style={{ fontSize: 11, color: esTodos ? C.text : C.mist, fontWeight: esTodos ? 700 : 400, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >Todos</button>
            <span style={{ color: C.border }}>·</span>
            <button
              onClick={() => setSectoresActivos(new Set(['__ninguno__']))}
              style={{ fontSize: 11, color: esNinguno ? C.text : C.mist, fontWeight: esNinguno ? 700 : 400, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >Ninguno</button>
          </div>
          {sectoresDisponibles.map(s => {
            const marcado = isMarcado(s);
            return (
              <label key={s} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
                cursor: 'pointer', fontSize: 13, color: marcado ? C.text : C.muted,
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <input type="checkbox" checked={marcado} onChange={() => toggleS(s)}
                  style={{ accentColor: C.indigo, width: 14, height: 14, cursor: 'pointer' }} />
                {s}
              </label>
            );
          })}
          <div style={{ padding: '8px 12px 4px', borderTop: `1px solid ${C.border}`, marginTop: 4 }}>
            <button onClick={() => setAbierto(false)} style={{
              width: '100%', padding: '6px', borderRadius: 6, fontSize: 12,
              background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600,
            }}>Aplicar</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// PESTAÑA CRM / OPORTUNIDADES
// ─────────────────────────────────────────────
const PestanaCRM = ({ oportunidades, presupuestos, proyectos, leads }) => {

  // Años disponibles — basados en oportunidades, presupuestos y proyectos
  const añosDisponibles = useMemo(() => {
    const set = new Set();
    oportunidades.forEach(o => { const y = getYear(o.fecha_entrada || o.creado_en); if (y) set.add(y); });
    presupuestos.forEach(p => { const y = getYear(p.fecha_presupuesto || p.creado_en); if (y) set.add(y); });
    proyectos.forEach(p => { const y = getYearProyecto(p); if (y) set.add(y); });
    return [...set].sort((a, b) => b - a);
  }, [oportunidades, presupuestos, proyectos]);

  const [añosActivos, setAñosActivos] = useState(() => [new Date().getFullYear()]);
  const [sectoresActivosCRM, setSectoresActivosCRM] = useState(new Set());
  const [vistasSector, setVistasSector] = useState({ op: 'num', presu: 'num', proy: 'num' });

  const sectoresDisponibles = useMemo(() => {
    const set = new Set();
    oportunidades.forEach(o => { if (o.sector) set.add(o.sector); });
    presupuestos.forEach(p => { if (p.sector) set.add(p.sector); });
    proyectos.forEach(p => { if (p.sector) set.add(p.sector); });
    return [...set].sort();
  }, [oportunidades, presupuestos, proyectos]);

  useEffect(() => {
    if (añosDisponibles.length > 0 && !añosDisponibles.some(y => añosActivos.includes(y))) {
      setAñosActivos([añosDisponibles[0]]);
    }
  }, [añosDisponibles]);

  const toggleAño = (y) => {
    setAñosActivos(prev =>
      prev.includes(y) ? (prev.length > 1 ? prev.filter(a => a !== y) : prev) : [...prev, y]
    );
  };

  const todosSectoresCRM = !sectoresActivosCRM.has('__ninguno__') && sectoresActivosCRM.size === 0;
  const ningunSectorCRM = sectoresActivosCRM.has('__ninguno__');

  // Filtrado por años activos y sectores múltiples
  const opFiltradas = useMemo(() => {
    let arr = oportunidades.filter(o => { const y = getYear(o.fecha_entrada || o.creado_en); return y && añosActivos.includes(y); });
    if (ningunSectorCRM) return [];
    if (!todosSectoresCRM) arr = arr.filter(o => sectoresActivosCRM.has(o.sector));
    return arr;
  }, [oportunidades, añosActivos, sectoresActivosCRM, todosSectoresCRM, ningunSectorCRM]);

  const presuFiltrados = useMemo(() => {
    let arr = presupuestos.filter(p => { const y = getYear(p.fecha_presupuesto || p.creado_en); return y && añosActivos.includes(y); });
    if (ningunSectorCRM) return [];
    if (!todosSectoresCRM) arr = arr.filter(p => sectoresActivosCRM.has(p.sector));
    return arr;
  }, [presupuestos, añosActivos, sectoresActivosCRM, todosSectoresCRM, ningunSectorCRM]);

  const proyFiltrados = useMemo(() => {
    let arr = proyectos.filter(p => { const y = getYearProyecto(p); return y && añosActivos.includes(y); });
    if (ningunSectorCRM) return [];
    if (!todosSectoresCRM) arr = arr.filter(p => sectoresActivosCRM.has(p.sector));
    return arr;
  }, [proyectos, añosActivos, sectoresActivosCRM, todosSectoresCRM, ningunSectorCRM]);

  // ── KPIs ── (sesión 31: mismas fórmulas que los módulos Oportunidades y Presupuestos)
  const totalOp = opFiltradas.length;
  const totalPresu = presuFiltrados.length;
  const totalProy = proyFiltrados.length;
  const nOp = (e) => opFiltradas.filter(o => o.estado === e).length;
  const opPresupuestadas = nOp('presupuestado') + nOp('aceptado') + nOp('rechazado');
  const opAceptadas = nOp('aceptado');
  const presAceptados = presuFiltrados.filter(p => p.estado === 'aceptado').length;
  const presRechazados = presuFiltrados.filter(p => p.estado === 'rechazado').length;
  const tasaPresupuestacion = pct(opPresupuestadas, totalOp);              // (presupuestado + aceptado + rechazado) / total
  const tasaAceptacion = pct(presAceptados, presAceptados + presRechazados); // aceptados / (aceptados + rechazados)
  const conversionGlobal = pct(opAceptadas, totalOp);                       // aceptado / total

  const volEncargado = proyFiltrados.reduce((s, p) => s + (p.importe_encargo || 0), 0);

  // Tiempo medio de conversión: de la entrada de la oportunidad al presupuesto.
  // Respeta los filtros de año y sector (sesión 31).
  const tiempoConversion = useMemo(() => {
    const pares = [];
    presuFiltrados.forEach(presu => {
      if (!presu.fecha_presupuesto || !presu.oportunidadId) return;
      const op = oportunidades.find(o => o.id === presu.oportunidadId);
      if (!op?.fecha_entrada) return;
      const dias = diffDias(op.fecha_entrada, presu.fecha_presupuesto);
      if (dias !== null && dias >= 0) pares.push(dias);
    });
    if (!pares.length) return null;
    return Math.round(pares.reduce((s, d) => s + d, 0) / pares.length);
  }, [presuFiltrados, oportunidades]);

  // ── Gráfico evolución anual ──
  const datosEvolucion = useMemo(() => {
    const todosAños = [...new Set([
      ...oportunidades.map(o => getYear(o.fecha_entrada || o.creado_en)),
      ...presupuestos.map(p => getYear(p.fecha_presupuesto || p.creado_en)),
      ...proyectos.map(p => getYearProyecto(p)).filter(Boolean),
    ])].filter(Boolean).sort();
    return todosAños.map(y => ({
      año: String(y),
      oportunidades: oportunidades.filter(o => getYear(o.fecha_entrada || o.creado_en) === y).length,
      presupuestos: presupuestos.filter(p => getYear(p.fecha_presupuesto || p.creado_en) === y).length,
      proyectos: proyectos.filter(p => getYearProyecto(p) === y).length,
      volumen: proyectos.filter(p => getYearProyecto(p) === y).reduce((s, p) => s + (p.importe_encargo || 0), 0),
    }));
  }, [oportunidades, presupuestos, proyectos]);

  // ── Gráfico estacionalidad mensual ──
  const datosEstacionalidad = useMemo(() => {
    const div = añosActivos.length || 1;
    return MESES.map((mes, i) => ({
      mes,
      oportunidades: +(opFiltradas.filter(o => getMonth(o.fecha_entrada || o.creado_en) === i).length / div).toFixed(1),
      presupuestos:  +(presuFiltrados.filter(p => getMonth(p.fecha_presupuesto || p.creado_en) === i).length / div).toFixed(1),
      proyectos:     +(proyFiltrados.filter(p => getMonth(p.fecha_inicio) === i).length / div).toFixed(1),
    }));
  }, [opFiltradas, presuFiltrados, proyFiltrados, añosActivos]);

  // ── Tartas por sector ──
  const sectorDataNum = (items, campo) => {
    const map = {};
    items.forEach(item => { const s = item[campo] || 'Sin sector'; map[s] = (map[s] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };
  const sectorDataVol = (items, campoSector, campoImporte) => {
    const map = {};
    items.forEach(item => {
      const s = item[campoSector] || 'Sin sector';
      map[s] = (map[s] || 0) + (item[campoImporte] || item.presupuesto_estimado || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };

  const renderPie = (items, campoSector, campoImporte, title, vistaKey) => {
    const vista = vistasSector[vistaKey];
    const data = vista === 'num'
      ? sectorDataNum(items, campoSector)
      : sectorDataVol(items, campoSector, campoImporte);
    const fmtTooltip = vista === 'num' ? fmt : fmtEur;

    return (
      <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 16px 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
          <ToggleVista value={vista} onChange={v => setVistasSector(prev => ({ ...prev, [vistaKey]: v }))} />
        </div>
        <div style={{ height: 200 }}>
          {data.length === 0 ? <SinDatos /> : (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} cx="40%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={2}>
                  {data.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<TooltipCustom formatValue={fmtTooltip} />} />
                <Legend layout="vertical" align="right" verticalAlign="middle"
                  formatter={(val) => <span style={{ fontSize: 11, color: C.muted }}>{val}</span>} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Filtros: años y sector */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: C.muted }}>Años:</span>
        {añosDisponibles.length === 0
          ? <span style={{ fontSize: 12, color: C.muted }}>Sin datos aún</span>
          : añosDisponibles.map(y => <YearPill key={y} year={y} active={añosActivos.includes(y)} onClick={toggleAño} />)
        }
        <DropdownSectores
          sectoresDisponibles={sectoresDisponibles}
          sectoresActivos={sectoresActivosCRM}
          setSectoresActivos={setSectoresActivosCRM}
        />
        {sectoresActivosCRM.size > 0 && (
          <button onClick={() => setSectoresActivosCRM(new Set())} style={{
            fontSize: 12, color: C.warning, background: 'transparent',
            border: `1px solid rgba(186,117,23,0.3)`, borderRadius: 6,
            padding: '4px 10px', cursor: 'pointer',
          }}>✕ Limpiar sector</button>
        )}
      </div>

      {/* KPIs fila 1 — embudo en números */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <KPICard label="Oportunidades" value={fmt(totalOp)} sub="Contactos con posible proyecto" />
        <KPICard label="Presupuestos" value={fmt(totalPresu)} color={C.violet} sub="Presupuestos formales enviados" />
        <KPICard label="Proyectos captados" value={fmt(totalProy)} color={C.positive} sub="Proyectos iniciados en el periodo" />
      </div>

      {/* KPIs fila 2 — conversiones */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <KPICard label="Tasa de presupuestación" value={`${tasaPresupuestacion}%`}
          sub={`${opPresupuestadas} de ${totalOp} oportunidades presupuestadas`} color={C.violet} />
        <KPICard label="Tasa de aceptación" value={`${tasaAceptacion}%`}
          sub={`${presAceptados} de ${presAceptados + presRechazados} presupuestos con respuesta`} color={C.violet} />
        <KPICard label="Conversión global" value={`${conversionGlobal}%`}
          sub={`${opAceptadas} de ${totalOp} oportunidades aceptadas`} color={C.mist} />
      </div>
      {/* KPIs fila 3 — resultado económico y velocidad */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
        <KPICard label="Volumen encargado" value={fmtEur(volEncargado)} color={C.positive}
          sub="Suma honorarios de proyectos captados" />
        <KPICard label="Tiempo medio conversión"
          value={tiempoConversion !== null ? `${tiempoConversion} días` : '--'}
          sub={tiempoConversion !== null ? 'De la oportunidad al presupuesto' : 'Sin datos suficientes'}
          color={C.mist} />
      </div>

      {/* Evolución anual */}
      <SectionTitle>Evolución anual</SectionTitle>
      <ChartCard height={320}>
        {datosEvolucion.length === 0 ? <SinDatos /> : (
          <ResponsiveContainer>
            <ComposedChart data={datosEvolucion} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="año" tick={{ fill: C.muted, fontSize: 12 }} axisLine={{ stroke: C.border }} tickLine={false} />
              <YAxis yAxisId="unidades" tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="volumen" orientation="right" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<TooltipCustom formatValue={(v, name) => name === 'Volumen' ? fmtEur(v) : fmt(v)} />} />
              <Legend formatter={(val) => <span style={{ fontSize: 12, color: C.muted }}>{val}</span>} />
              <Bar yAxisId="volumen" dataKey="volumen" name="Volumen" fill={C.indigo} opacity={0.25} radius={[3,3,0,0]} barSize={28} />
              <Line yAxisId="unidades" type="monotone" dataKey="oportunidades" stroke={C.indigo} strokeWidth={2.5} dot={{ fill: C.indigo, r: 4 }} activeDot={{ r: 6 }} name="Oportunidades" />
              <Line yAxisId="unidades" type="monotone" dataKey="presupuestos" stroke={C.violet} strokeWidth={2.5} dot={{ fill: C.violet, r: 4 }} activeDot={{ r: 6 }} name="Presupuestos" />
              <Line yAxisId="unidades" type="monotone" dataKey="proyectos" stroke={C.positive} strokeWidth={2.5} dot={{ fill: C.positive, r: 4 }} activeDot={{ r: 6 }} name="Proyectos" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Estacionalidad mensual */}
      <SectionTitle>
        Estacionalidad mensual{añosActivos.length > 1 ? ` (media ${añosActivos.join(', ')})` : ` (${añosActivos[0]})`}
      </SectionTitle>
      <ChartCard height={260}>
        <ResponsiveContainer>
          <BarChart data={datosEstacionalidad} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barSize={8}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="mes" tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: C.muted, fontSize: 12 }} axisLine={false} tickLine={false} />
            <Tooltip content={<TooltipCustom />} cursor={{ fill: 'rgba(83,74,183,0.1)', radius: 4 }} />
            <Legend formatter={(val) => <span style={{ fontSize: 12, color: C.muted, textTransform: 'capitalize' }}>{val}</span>} />
            <Bar dataKey="oportunidades" fill={C.indigo} radius={[3,3,0,0]} name="Oportunidades" />
            <Bar dataKey="presupuestos" fill={C.violet} radius={[3,3,0,0]} name="Presupuestos" />
            <Bar dataKey="proyectos" fill={C.positive} radius={[3,3,0,0]} name="Proyectos" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Sectores */}
      <SectionTitle>Distribución por sector</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {renderPie(opFiltradas, 'sector', 'presupuesto_estimado', 'Oportunidades por sector', 'op')}
        {renderPie(presuFiltrados, 'sector', 'importe', 'Presupuestos por sector', 'presu')}
        {renderPie(proyFiltrados, 'sector', 'importe_encargo', 'Proyectos por sector', 'proy')}
      </div>

      {/* Conversión por fuente */}
      <SectionTitle>Conversión por fuente de lead</SectionTitle>
      <TablaPorFuente oportunidades={opFiltradas} />

    </div>
  );
};

// ─── Tabla de conversión por fuente ──────────────────────────────────────────
const TablaPorFuente = ({ oportunidades }) => {
  // Sesión 31: se mide por el estado de cada oportunidad (como el módulo Oportunidades).
  // Antes buscaba proyectos por un enlace que casi ningún proyecto tiene y salía 0.
  const fuentes = useMemo(() => {
    const set = new Set(oportunidades.map(o => o.fuente || 'Sin fuente'));
    return [...set].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [oportunidades]);
  if (fuentes.length === 0) return (
    <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>Sin datos de fuente en el período seleccionado.</div>
  );
  const filas = fuentes.map(fuente => {
    const ops = oportunidades.filter(o => (o.fuente || 'Sin fuente') === fuente);
    const presu = ops.filter(o => ['presupuestado', 'aceptado', 'rechazado'].includes(o.estado)).length;
    const acept = ops.filter(o => o.estado === 'aceptado').length;
    const convPresu = ops.length > 0 ? (presu / ops.length) * 100 : null;
    const convProy  = presu > 0 ? (acept / presu) * 100 : null;
    const convTotal = ops.length > 0 ? (acept / ops.length) * 100 : null;
    return { fuente, ops: ops.length, presu, projs: acept, convPresu, convProy, convTotal };
  }).sort((a, b) => b.ops - a.ops); // ordenar por volumen de oportunidades
  const thStyle = { padding: '8px 12px', textAlign: 'left', color: C.muted, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${C.border}` };
  const tdStyle = { padding: '12px', borderBottom: `1px solid ${C.border}`, fontSize: 13 };

  const colorConv = (v) => v === null ? C.muted : v >= 40 ? C.positive : v >= 20 ? C.violet : C.negative;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Fuente</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Oportunidades</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Presupuestadas</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Aceptadas</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Conv. Op→Presu</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Conv. Presu→Acept</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Conv. total</th>
          </tr>
        </thead>
        <tbody>
          {filas.map(f => (
            <tr key={f.fuente}>
              <td style={{ ...tdStyle, color: C.text, fontWeight: 500 }}>{f.fuente}</td>
              <td style={{ ...tdStyle, textAlign: 'center', color: C.text }}>{f.ops}</td>
              <td style={{ ...tdStyle, textAlign: 'center', color: C.muted }}>{f.presu}</td>
              <td style={{ ...tdStyle, textAlign: 'center', color: C.muted }}>{f.projs}</td>
              <td style={{ ...tdStyle, textAlign: 'center', color: colorConv(f.convPresu), fontWeight: 600 }}>
                {f.convPresu !== null ? fmtPct(f.convPresu) : '—'}
              </td>
              <td style={{ ...tdStyle, textAlign: 'center', color: colorConv(f.convProy), fontWeight: 600 }}>
                {f.convProy !== null ? fmtPct(f.convProy) : '—'}
              </td>
              <td style={{ ...tdStyle, textAlign: 'center', color: colorConv(f.convTotal), fontWeight: 700 }}>
                {f.convTotal !== null ? fmtPct(f.convTotal) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Dado un array de registros de horas de un proyecto y el tarifasMap,
// calcula el coste personal total cruzando cada registro con la tarifa vigente en su fecha.
const calcularCostePersonal = (registrosDelProyecto, tarifasMap) => {
  if (!registrosDelProyecto.length || !Object.keys(tarifasMap).length) return null;
  let total = 0;
  let alguno = false;
  registrosDelProyecto.forEach(r => {
    const uid = r.empleadoId;
    const entrada = tarifasMap[uid];
    if (!entrada?.tarifas?.length) return;
    const fechaReg = typeof r.fecha === 'string' ? r.fecha : (r.fecha?.toDate ? r.fecha.toDate().toISOString().slice(0,10) : null);
    if (!fechaReg) return;
    // Buscar tarifa vigente en la fecha del registro
    const tarifa = entrada.tarifas.find(t => {
      const desde = t.fecha_inicio || '';
      const hasta = t.fecha_fin || '9999-12-31';
      return fechaReg >= desde && fechaReg < hasta;
    });
    if (!tarifa?.coste_hora) return;
    total += (r.horas || 0) * tarifa.coste_hora;
    alguno = true;
  });
  return alguno ? total : null;
};


// ─────────────────────────────────────────────
// PESTAÑA RENTABILIDADES — columnas tabla compartida con PestanaProyectos
// ─────────────────────────────────────────────
const COLUMNAS_TABLA = [
  { key: 'codigo',        label: 'Código',       defecto: true  },
  { key: 'nombre',        label: 'Proyecto',     defecto: true  },
  { key: 'cliente',       label: 'Cliente',      defecto: true  },
  { key: 'sector',        label: 'Sector',       defecto: true  },
  { key: 'año',           label: 'Año',          defecto: true  },
  { key: 'superficie',    label: 'Sup. m²',      defecto: false },
  { key: 'encargo',       label: 'Encargo',      defecto: true  },
  { key: 'facturado',     label: 'Facturado',    defecto: true  },
  { key: 'horas',         label: 'Horas',        defecto: true  },
  { key: 'costePersonal', label: 'C. Personal',  defecto: false },
  { key: 'costeProd',     label: 'C. Produc.',   defecto: true  },
  { key: 'ventaM2',       label: 'Venta/m²',     defecto: false },
  { key: 'costeM2',       label: 'Coste/m²',     defecto: false },
  { key: 'ventaHr',       label: 'Venta/hr',     defecto: false },
  { key: 'costeHr',       label: 'Coste/hr',     defecto: false },
  { key: 'resultado',     label: 'Resultado',    defecto: true  },
  { key: 'margen',        label: 'Margen %',     defecto: true  },
  { key: 'velocidad',     label: 'Veloc. hr/m²', defecto: false },
];

// Tick personalizado para el eje Y del gráfico de proyectos (código + nombre)
const TickProyecto = ({ x, y, payload }) => {
  const partes = (payload.value || '').split('|');
  const codigo = partes[0] || '';
  const nombre = partes[1] || '';
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={-4} textAnchor="end" fill={C.violet} fontSize={12} fontWeight={600}>{codigo}</text>
      <text x={0} y={10} textAnchor="end" fill={C.muted} fontSize={10}>{nombre.substring(0, 18)}{nombre.length > 18 ? '…' : ''}</text>
    </g>
  );
};

// ─────────────────────────────────────────────
// Sesión 33 · avisos de carga de horas (cada pestaña lee solo los trozos de horas que necesita)
// ─────────────────────────────────────────────
const CargandoHoras = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: C.muted, padding: '40px 0' }}>
    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${C.indigo}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    Cargando registros de horas…
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const ErrorHoras = ({ texto }) => (
  <div style={{ padding: 24, color: C.negative, background: C.surface2, borderRadius: 12, margin: '16px 0' }}>{texto}</div>
);

// Encima de una pestaña ya visible: se están leyendo horas nuevas (las cifras se actualizan al terminar) o falló la lectura.
const AvisoHoras = ({ horas }) => {
  if (horas.error) return <ErrorHoras texto={horas.error} />;
  if (horas.listo) return null;
  return <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>Cargando horas de la selección… las cifras se actualizan al terminar.</div>;
};

// ─────────────────────────────────────────────
// PESTAÑA EMPRESA — análisis anual sobre proyectos finalizados
// ─────────────────────────────────────────────
const PestanaEmpresa = ({ proyectos, clientes, tarifasMap, kpisEmpresa }) => {
  const añosDisponibles = useMemo(() => {
    const set = new Set(proyectos.filter(p => p.estado === 'finalizado').map(p => getYear(p.fecha_fin_obra || p.fecha_entrega_proyecto || p.creado_en)).filter(Boolean));
    return [...set].sort((a, b) => b - a);
  }, [proyectos]);

  const [añosActivos, setAñosActivos] = useState(() => [new Date().getFullYear()]);

  useEffect(() => {
    if (añosDisponibles.length > 0 && !añosDisponibles.some(y => añosActivos.includes(y))) {
      setAñosActivos([añosDisponibles[0]]);
    }
  }, [añosDisponibles]);

  const toggleAño = (y) => setAñosActivos(prev =>
    prev.includes(y) ? (prev.length > 1 ? prev.filter(a => a !== y) : prev) : [...prev, y]
  );

  // Sesión 33: horas de los años elegidos (horas del equipo) + TODAS las horas de los proyectos finalizados
  // en esos años (su coste de personal incluye las horas de cualquier año).
  const idsFinalizados = useMemo(() => proyectos.filter(p => {
    if (p.estado !== 'finalizado') return false;
    const año = getYear(p.fecha_fin_obra || p.fecha_entrega_proyecto || p.creado_en);
    return año && añosActivos.includes(año);
  }).map(p => p.id), [proyectos, añosActivos]);
  const horas = useHoras({ anios: añosActivos, proyectos: idsFinalizados });
  const registrosHoras = horas.registros;

  const clienteMap = useMemo(() => {
    const m = {};
    clientes.forEach(c => { m[c.id] = c.razon_social || `${c.nombre || ''} ${c.apellidos || ''}`.trim(); });
    return m;
  }, [clientes]);

  const registrosPorProyecto = useMemo(() => {
    const m = {};
    registrosHoras.forEach(r => {
      const pid = r.proyectoId || r.proyecto_id;
      if (pid) { if (!m[pid]) m[pid] = []; m[pid].push(r); }
    });
    return m;
  }, [registrosHoras]);

  // Proyectos finalizados en los años seleccionados (por fecha_fin_obra)
  const proyFinalizados = useMemo(() => {
    return proyectos
      .filter(p => {
        if (p.estado !== 'finalizado') return false;
        const año = getYear(p.fecha_fin_obra || p.fecha_entrega_proyecto || p.creado_en);
        return año && añosActivos.includes(año);
      })
      .map(p => {
        const base = p.importe_facturado || p.importe_encargo || 0;
        const regs = registrosPorProyecto[p.id] || [];
        const costePersonal = calcularCostePersonal(regs, tarifasMap);
        const costeProd = p.costes_produccion || 0;
        const costeTotal = (costePersonal || 0) + costeProd;
        const resultado = base - costeTotal;
        const margen = base > 0 ? (resultado / base) * 100 : null;
        const sector = p.sector || 'Sin sector';
        return { ...p, base, costePersonal, costeProd, costeTotal, resultado, margen, sector, cliente: clienteMap[p.clienteId] || '--' };
      });
  }, [proyectos, añosActivos, registrosPorProyecto, tarifasMap, clienteMap]);

  // Proyectos activos NO incluidos (en curso o entregados)
  const proyActivos = proyectos.filter(p => p.estado === 'en_curso' || p.estado === 'entregado');

  // KPIs negocio
  const kpiTotal = proyFinalizados.length;
  const kpiVolumen = proyFinalizados.reduce((s, p) => s + p.base, 0);
  const kpiResultado = proyFinalizados.reduce((s, p) => s + p.resultado, 0);
  const kpiMargen = kpiVolumen > 0 ? (kpiResultado / kpiVolumen) * 100 : null;
  const hayTarifas = proyFinalizados.some(p => p.costePersonal !== null);

  // KPIs equipo — horas registradas en los años seleccionados en cualquier proyecto
  const horasEquipoAño = useMemo(() => {
    return registrosHoras.filter(r => {
      if (!r.proyectoId && !r.proyecto_id) return false; // excluir internas
      const año = getYear(r.fecha);
      return año && añosActivos.includes(año);
    }).reduce((s, r) => s + (r.horas || 0), 0);
  }, [registrosHoras, añosActivos]);

  // Datos por sector (sobre proyectos finalizados)
  const datosSector = useMemo(() => {
    const map = {};
    proyFinalizados.forEach(p => {
      if (!map[p.sector]) map[p.sector] = { sector: p.sector, facturado: 0, resultado: 0, proyectos: 0 };
      map[p.sector].facturado += p.base;
      map[p.sector].resultado += p.resultado;
      map[p.sector].proyectos += 1;
    });
    return Object.values(map)
      .map(s => ({ ...s, margen: s.facturado > 0 ? (s.resultado / s.facturado) * 100 : 0 }))
      .sort((a, b) => b.facturado - a.facturado);
  }, [proyFinalizados]);

  if (horas.primeraCarga) return horas.error ? <ErrorHoras texto={horas.error} /> : <CargandoHoras />;

  return (
    <div>
      <AvisoHoras horas={horas} />
      {/* Filtro de años */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: C.muted }}>Años:</span>
        {añosDisponibles.length === 0
          ? <span style={{ fontSize: 12, color: C.muted }}>Sin proyectos finalizados aún</span>
          : añosDisponibles.map(y => <YearPill key={y} year={y} active={añosActivos.includes(y)} onClick={toggleAño} />)
        }
      </div>

      {/* Aviso fijo — solo proyectos finalizados */}
      <div style={{ marginBottom: 20, padding: '9px 14px', borderRadius: 8, background: 'rgba(83,74,183,0.08)', border: `1px solid ${C.border}`, fontSize: 12, color: C.mist, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>ℹ</span>
        <span>
          Incluye solo proyectos <strong>finalizados</strong> en {añosActivos.length === 1 ? `el año ${añosActivos[0]}` : `los años ${añosActivos.join(', ')}`}.
          {proyActivos.length > 0 && <span style={{ color: C.muted }}> {proyActivos.length} proyecto{proyActivos.length > 1 ? 's' : ''} en curso o entregado{proyActivos.length > 1 ? 's' : ''} no incluido{proyActivos.length > 1 ? 's' : ''} — ver pestaña Proyectos.</span>}
        </span>
      </div>

      {/* KPIs fila 1 — negocio cerrado */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <KPICard label="Proyectos finalizados" value={fmt(kpiTotal)} />
        <KPICard label="Volumen facturado" value={fmtEur(kpiVolumen)} color={C.positive}
          sub="Importe facturado (o encargo si no hay facturado)" />
        <KPICard label="Resultado" value={fmtEur(kpiResultado)}
          color={kpiResultado >= 0 ? C.positive : C.negative}
          sub={!hayTarifas ? '⚠ Parcial: sin coste personal — configura tarifas' : 'Volumen − coste personal − costes producción'} />
        <KPICard label="Margen medio" value={kpiMargen !== null ? fmtPct(kpiMargen) : '--'}
          color={kpiMargen !== null ? colorMargen(kpiMargen) : C.muted}
          sub="Resultado total / volumen total (ponderado)" />
      </div>

      {/* KPIs fila 2 — equipo */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {kpisEmpresa && (
          <>
            <KPICard small label="Horas disponibles equipo" value={`${fmtHoras(kpisEmpresa.horasDisponiblesAnio)} h`} color={C.mist}
              sub="Horas anuales contratadas (tarifas vigentes)" />
            <KPICard small label="Horas en proyectos" value={horasEquipoAño > 0 ? `${fmtHoras(horasEquipoAño)} h` : '--'} color={C.mist}
              sub={`Registradas en ${añosActivos.length === 1 ? añosActivos[0] : 'años seleccionados'}`} />
            <KPICard small label="Tasa de utilización"
              value={kpisEmpresa.horasDisponiblesAnio > 0 ? `${((horasEquipoAño / kpisEmpresa.horasDisponiblesAnio) * 100).toFixed(1)}%` : '--'}
              color={(() => { const t = horasEquipoAño / kpisEmpresa.horasDisponiblesAnio * 100; return t >= 75 ? C.positive : t >= 60 ? C.warning : C.negative; })()}
              sub="Ref. sector: 60–65% estudio / 75–85% técnico" />
            <KPICard small label="Coste/hr empresa" value={fmtEur2(kpisEmpresa.costeHrEmpresa)} color={C.negative}
              sub={kpisEmpresa.usaEstructuraAnterior ? `⚠ Nóminas ${kpisEmpresa.anioNominas} + estructura estimada ${kpisEmpresa.anioEstructura}` : `Nóminas ${kpisEmpresa.anioNominas} + estructura ${kpisEmpresa.anioEstructura}`} />
            <KPICard small label="Venta/hr empresa"
              value={kpisEmpresa.ventaHrEmpresa !== null ? fmtEur2(kpisEmpresa.ventaHrEmpresa) : '--'}
              color={kpisEmpresa.ventaHrEmpresa !== null && kpisEmpresa.ventaHrEmpresa > kpisEmpresa.costeHrEmpresa ? C.positive : C.warning}
              sub={`Volumen ${kpisEmpresa.anioNominas} ÷ ${fmt(kpisEmpresa.horasDisponiblesAnio)} h disponibles`} />
          </>
        )}
        {!kpisEmpresa && (
          <div style={{ padding: '9px 14px', borderRadius: 8, background: 'rgba(186,117,23,0.08)', border: '1px solid rgba(186,117,23,0.2)', fontSize: 12, color: '#e8a93a' }}>
            ⚠ Introduce los gastos de estructura en <strong>Configuración → Estructura costes</strong> para ver el coste/hora real de la empresa.
          </div>
        )}
      </div>

      {/* Gráficos por sector */}
      {datosSector.length > 0 && (
        <>
          <SectionTitle>Análisis por sector — proyectos finalizados</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 28 }}>

            <ChartCard title="Facturación por sector" height={220}>
              <ResponsiveContainer>
                <BarChart data={datosSector} margin={{ top: 5, right: 10, bottom: 40, left: 0 }} barSize={24}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="sector" tick={{ fill: C.muted, fontSize: 10, angle: -30, textAnchor: 'end' }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<TooltipCustom formatValue={fmtEur} />} cursor={{ fill: 'rgba(83,74,183,0.12)', radius: 4 }} />
                  <Bar dataKey="facturado" name="Facturado" radius={[4,4,0,0]}>
                    {datosSector.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Resultado por sector" height={220}>
              <ResponsiveContainer>
                <BarChart data={datosSector} margin={{ top: 5, right: 10, bottom: 40, left: 0 }} barSize={24}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="sector" tick={{ fill: C.muted, fontSize: 10, angle: -30, textAnchor: 'end' }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <ReferenceLine y={0} stroke={C.border} />
                  <Tooltip content={<TooltipCustom formatValue={fmtEur} />} cursor={{ fill: 'rgba(83,74,183,0.12)', radius: 4 }} />
                  <Bar dataKey="resultado" name="Resultado" radius={[4,4,0,0]}>
                    {datosSector.map((entry, i) => <Cell key={i} fill={entry.resultado >= 0 ? C.positive : C.negative} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Margen % por sector" height={220}>
              <ResponsiveContainer>
                <BarChart data={datosSector} margin={{ top: 5, right: 10, bottom: 40, left: 0 }} barSize={24}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="sector" tick={{ fill: C.muted, fontSize: 10, angle: -30, textAnchor: 'end' }} axisLine={false} tickLine={false} interval={0} />
                  <YAxis tick={{ fill: C.muted, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v.toFixed(0)}%`} />
                  <ReferenceLine y={35} stroke={C.warning} strokeDasharray="4 4" label={{ value: '35%', fill: C.warning, fontSize: 10, position: 'right' }} />
                  <ReferenceLine y={50} stroke={C.positive} strokeDasharray="4 4" label={{ value: '50%', fill: C.positive, fontSize: 10, position: 'right' }} />
                  <Tooltip content={<TooltipCustom formatValue={fmtPct} />} cursor={{ fill: 'rgba(83,74,183,0.12)', radius: 4 }} />
                  <Bar dataKey="margen" name="Margen %" radius={[4,4,0,0]}>
                    {datosSector.map((entry, i) => <Cell key={i} fill={colorMargen(entry.margen)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

          </div>
        </>
      )}

      {kpiTotal === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px', color: C.muted, fontSize: 14 }}>
          Sin proyectos finalizados en {añosActivos.length === 1 ? `el año ${añosActivos[0]}` : `los años seleccionados`}.
        </div>
      )}
    </div>
  );
};

// legacy alias — eliminado: PestanaRentabilidades ya no existe
const PestanaRentabilidades = PestanaEmpresa;

// ─────────────────────────────────────────────
// PESTAÑA PROYECTOS — tabla detallada + KPIs de media
// ─────────────────────────────────────────────
const PestanaProyectos = ({ proyectos, clientes, tarifasMap, kpisEmpresa }) => {
  const añosDisponibles = useMemo(() => {
    const set = new Set(proyectos.map(p => getYear(p.fecha_inicio || p.creado_en)).filter(Boolean));
    return [...set].sort((a, b) => b - a);
  }, [proyectos]);

  const sectoresDisponibles = useMemo(() => {
    const set = new Set(proyectos.map(p => p.sector).filter(Boolean));
    return [...set].sort();
  }, [proyectos]);

  const ESTADOS_P = [
    { key: 'en_curso',   label: 'En curso' },
    { key: 'entregado',  label: 'Entregado' },
    { key: 'finalizado', label: 'Finalizado' },
  ];
  // Sesión 33: arranca con los proyectos activos; Finalizado se marca a mano (carga sus horas al marcarlo).
  const [estadosActivos, setEstadosActivos] = useState(new Set(['en_curso', 'entregado']));
  const toggleEstado = (key) => setEstadosActivos(prev => {
    const n = new Set(prev);
    if (n.has(key)) { if (n.size > 1) n.delete(key); } else n.add(key);
    return n;
  });

  // Sesión 33: solo se leen las horas de los proyectos de los estados marcados.
  const idsVisibles = useMemo(() => proyectos.filter(p => estadosActivos.has(p.estado)).map(p => p.id), [proyectos, estadosActivos]);
  const horas = useHoras({ proyectos: idsVisibles });
  const registrosHoras = horas.registros;

  const [sectoresActivos, setSectoresActivos] = useState(new Set());
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [filtroActivo, setFiltroActivo] = useState(null);
  const [sugerenciasAbiertas, setSugerenciasAbiertas] = useState(false);
  const refBuscador = React.useRef(null);

  // Columnas activas persistidas en localStorage
  const [colsActivas, setColsActivas] = useState(() => {
    try {
      const saved = localStorage.getItem('metron_cols_proyectos');
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set(COLUMNAS_TABLA.filter(c => c.defecto).map(c => c.key));
  });
  const toggleColumna = (key) => setColsActivas(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    try { localStorage.setItem('metron_cols_proyectos', JSON.stringify([...n])); } catch {}
    return n;
  });

  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [ordenCol, setOrdenCol] = useState('facturado');
  const [ordenDir, setOrdenDir] = useState('desc');

  const clienteMap = useMemo(() => {
    const m = {};
    clientes.forEach(c => { m[c.id] = c.razon_social || `${c.nombre || ''} ${c.apellidos || ''}`.trim(); });
    return m;
  }, [clientes]);

  const registrosPorProyecto = useMemo(() => {
    const m = {};
    registrosHoras.forEach(r => {
      const pid = r.proyectoId || r.proyecto_id;
      if (pid) { if (!m[pid]) m[pid] = []; m[pid].push(r); }
    });
    return m;
  }, [registrosHoras]);

  const proyectosEnriquecidos = useMemo(() => {
    return proyectos.map(p => {
      const año = getYear(p.fecha_inicio || p.creado_en);
      const base = p.estado === 'finalizado'
        ? (p.importe_facturado || p.importe_encargo || 0)
        : (p.importe_encargo || 0);
      const regsProyecto = registrosPorProyecto[p.id] || [];
      const horas = regsProyecto.reduce((s, r) => s + (r.horas || 0), 0);
      const costePersonal = calcularCostePersonal(regsProyecto, tarifasMap);
      const costeProd = p.costes_produccion || 0;
      const costeTotal = (costePersonal || 0) + costeProd;
      const resultado = base - costeTotal;
      const margen = base > 0 ? (resultado / base) * 100 : null;
      const velocidad = horas > 0 && p.superficie > 0 ? +(horas / p.superficie).toFixed(2) : null;
      const cliente = clienteMap[p.clienteId] || '--';
      const esEstimado = p.estado === 'en_curso' || p.estado === 'entregado';
      const ventaM2 = p.superficie > 0 ? base / p.superficie : null;
      const costeM2 = p.superficie > 0 && costeTotal > 0 ? costeTotal / p.superficie : null;
      const ventaHr = horas > 0 ? base / horas : null;
      const costeHr = horas > 0 && costeTotal > 0 ? costeTotal / horas : null;
      return { ...p, año, base, horas, costePersonal, costeProd, costeTotal, resultado, margen, velocidad, cliente, esEstimado, ventaM2, costeM2, ventaHr, costeHr };
    });
  }, [proyectos, registrosPorProyecto, clienteMap, tarifasMap]);

  const todosLosSectores = !sectoresActivos.has('__ninguno__') && sectoresActivos.size === 0;
  const ningunSector = sectoresActivos.has('__ninguno__');

  const proyFiltrados = useMemo(() => {
    let arr = proyectosEnriquecidos.filter(p => estadosActivos.has(p.estado));
    if (ningunSector) return [];
    if (!todosLosSectores) arr = arr.filter(p => sectoresActivos.has(p.sector));
    if (filtroActivo) {
      if (filtroActivo.tipo === 'proyecto') arr = arr.filter(p => p.id === filtroActivo.id);
      if (filtroActivo.tipo === 'cliente') arr = arr.filter(p => p.clienteId === filtroActivo.id);
    }
    return arr;
  }, [proyectosEnriquecidos, estadosActivos, sectoresActivos, todosLosSectores, ningunSector, filtroActivo]);

  const sugerencias = useMemo(() => {
    const q = norm(textoBusqueda.trim());
    if (q.length < 2) return { proyectos: [], clientes: [] };
    const sugerProy = proyectosEnriquecidos.filter(p => norm(p.nombre).includes(q) || norm(p.codigo).includes(q)).slice(0, 5).map(p => ({ tipo: 'proyecto', id: p.id, label: p.nombre || p.codigo, sub: p.codigo }));
    const clientesVistos = new Set();
    const clientesUnicos = [];
    proyectosEnriquecidos.forEach(p => {
      if (p.clienteId && !clientesVistos.has(p.clienteId) && norm(p.cliente).includes(q)) {
        clientesVistos.add(p.clienteId);
        clientesUnicos.push({ tipo: 'cliente', id: p.clienteId, label: p.cliente, sub: 'Cliente' });
      }
    });
    return { proyectos: sugerProy, clientes: clientesUnicos.slice(0, 5) };
  }, [textoBusqueda, proyectosEnriquecidos]);

  useEffect(() => {
    if (!sugerenciasAbiertas) return;
    const handler = (e) => { if (refBuscador.current && !refBuscador.current.contains(e.target)) setSugerenciasAbiertas(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sugerenciasAbiertas]);

  const proyOrdenados = useMemo(() => {
    return [...proyFiltrados].sort((a, b) => {
      let va = a[ordenCol], vb = b[ordenCol];
      if (ordenCol === 'facturado') { va = a.base; vb = b.base; }
      if (ordenCol === 'encargo') { va = a.importe_encargo; vb = b.importe_encargo; }
      if (typeof va === 'string') return ordenDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return ordenDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
  }, [proyFiltrados, ordenCol, ordenDir]);

  // KPIs de media ponderada
  const kpiVolumen = proyFiltrados.reduce((s, p) => s + p.base, 0);
  const kpiResultado = proyFiltrados.reduce((s, p) => s + p.resultado, 0);
  const kpiMargen = kpiVolumen > 0 ? (kpiResultado / kpiVolumen) * 100 : null;
  const totalSuperficie = proyFiltrados.reduce((s, p) => s + (p.superficie || 0), 0);
  const totalHoras = proyFiltrados.reduce((s, p) => s + (p.horas || 0), 0);
  const totalCoste = proyFiltrados.reduce((s, p) => s + p.costeTotal, 0);
  const mediaVentaM2 = totalSuperficie > 0 ? kpiVolumen / totalSuperficie : null;
  const mediaCosteM2 = totalSuperficie > 0 && totalCoste > 0 ? totalCoste / totalSuperficie : null;
  const mediaVentaHr = totalHoras > 0 ? kpiVolumen / totalHoras : null;
  const mediaCosteHr = totalHoras > 0 && totalCoste > 0 ? totalCoste / totalHoras : null;
  const mediaVelocidad = totalSuperficie > 0 && totalHoras > 0 ? totalHoras / totalSuperficie : null;
  const hayTarifas = proyFiltrados.some(p => p.costePersonal !== null);

  const toggleOrden = (key) => { if (ordenCol === key) setOrdenDir(d => d === 'asc' ? 'desc' : 'asc'); else { setOrdenCol(key); setOrdenDir('desc'); } };
  const iconOrden = (key) => { if (ordenCol !== key) return <span style={{ opacity: 0.3 }}>↕</span>; return ordenDir === 'asc' ? '↑' : '↓'; };

  if (horas.primeraCarga) return horas.error ? <ErrorHoras texto={horas.error} /> : <CargandoHoras />;

  return (
    <div>
      <AvisoHoras horas={horas} />
      {/* Filtros — estado + buscador + sectores */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20, alignItems: 'flex-start' }}>
        {/* Pills de estado */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {ESTADOS_P.map(e => (
            <button key={e.key} onClick={() => toggleEstado(e.key)} style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${estadosActivos.has(e.key) ? C.indigo : C.border}`,
              background: estadosActivos.has(e.key) ? C.indigo : 'transparent',
              color: estadosActivos.has(e.key) ? '#fff' : C.muted,
              fontWeight: estadosActivos.has(e.key) ? 600 : 400, transition: 'all 0.15s',
            }}>{e.label}</button>
          ))}
        </div>

        <div ref={refBuscador} style={{ position: 'relative', minWidth: 260 }}>
          {filtroActivo ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, background: filtroActivo.tipo === 'proyecto' ? 'rgba(83,74,183,0.2)' : 'rgba(29,158,117,0.15)', border: `1px solid ${filtroActivo.tipo === 'proyecto' ? C.indigo : C.positive}`, fontSize: 13, color: C.text }}>
              <span style={{ fontSize: 11, color: filtroActivo.tipo === 'proyecto' ? C.mist : C.positive, textTransform: 'uppercase' }}>{filtroActivo.tipo === 'proyecto' ? '📁' : '👤'} {filtroActivo.tipo}</span>
              <span style={{ fontWeight: 600 }}>{filtroActivo.label}</span>
              <button onClick={() => { setFiltroActivo(null); setTextoBusqueda(''); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14 }}>✕</button>
            </div>
          ) : (
            <>
              <input placeholder="Buscar proyecto o cliente..." value={textoBusqueda}
                onChange={e => { setTextoBusqueda(e.target.value); setSugerenciasAbiertas(true); }}
                onFocus={() => textoBusqueda.length >= 2 && setSugerenciasAbiertas(true)}
                style={{ padding: '8px 14px', borderRadius: 8, width: '100%', boxSizing: 'border-box', border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 13, outline: 'none' }}
              />
              {sugerenciasAbiertas && (sugerencias.proyectos.length > 0 || sugerencias.clientes.length > 0) && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 4, overflow: 'hidden' }}>
                  {sugerencias.proyectos.length > 0 && (<>
                    <div style={{ padding: '6px 14px 4px', fontSize: 11, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>Proyectos</div>
                    {sugerencias.proyectos.map(s => (
                      <div key={s.id} onMouseDown={() => { setFiltroActivo(s); setTextoBusqueda(''); setSugerenciasAbiertas(false); }} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: C.text, fontWeight: 500 }}>{s.label}</span>
                        <span style={{ fontSize: 11, color: C.violet, fontFamily: 'monospace' }}>{s.sub}</span>
                      </div>
                    ))}
                  </>)}
                  {sugerencias.clientes.length > 0 && (<>
                    <div style={{ padding: '6px 14px 4px', fontSize: 11, color: C.muted, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}`, borderTop: sugerencias.proyectos.length > 0 ? `1px solid ${C.border}` : 'none' }}>Clientes</div>
                    {sugerencias.clientes.map(s => (
                      <div key={s.id} onMouseDown={() => { setFiltroActivo(s); setTextoBusqueda(''); setSugerenciasAbiertas(false); }} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(29,158,117,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ color: C.text, fontWeight: 500 }}>{s.label}</span>
                      </div>
                    ))}
                  </>)}
                </div>
              )}
            </>
          )}
        </div>
        <DropdownSectores sectoresDisponibles={sectoresDisponibles} sectoresActivos={sectoresActivos} setSectoresActivos={setSectoresActivos} />
        {(filtroActivo || !todosLosSectores) && (
          <button onClick={() => { setFiltroActivo(null); setTextoBusqueda(''); setSectoresActivos(new Set()); }} style={{ fontSize: 12, color: C.warning, background: 'transparent', border: `1px solid rgba(186,117,23,0.3)`, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>✕ Limpiar filtros</button>
        )}
      </div>

      {/* KPIs fila 1 — eficiencia por m² y por hora */}
      {!hayTarifas && (
        <div style={{ background: 'rgba(186,117,23,0.1)', border: `1px solid rgba(186,117,23,0.25)`, borderRadius: 8, padding: '9px 14px', fontSize: 12, color: '#e8a93a', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>⚠</span>
          <span>Coste/m² y Coste/hr son <strong>parciales</strong> — se incluirá el coste de personal cuando se configuren tarifas en <strong>Configuración</strong>.</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <KPICard small label="Venta media/m²" value={mediaVentaM2 !== null ? fmtEur2(mediaVentaM2) : '--'} color={C.mist} sub={totalSuperficie > 0 ? `${fmt(totalSuperficie)} m\u00b2 totales` : 'Sin superficie'} />
        <KPICard small label="Coste medio/m²" value={mediaCosteM2 !== null ? fmtEur2(mediaCosteM2) : '--'} color={mediaCosteM2 !== null ? C.negative : C.muted} sub={!hayTarifas ? '\u26a0 Parcial sin tarifas' : undefined} />
        <KPICard small label="Venta media/hr" value={mediaVentaHr !== null ? fmtEur2(mediaVentaHr) : '--'} color={C.mist} sub={totalHoras > 0 ? `${fmtHoras(totalHoras)} h totales` : 'Sin horas'} />
        <KPICard small label="Coste medio/hr" value={mediaCosteHr !== null ? fmtEur2(mediaCosteHr) : '--'} color={mediaCosteHr !== null ? C.negative : C.muted} sub={!hayTarifas ? '\u26a0 Parcial sin tarifas' : undefined} />
        <KPICard small label="Velocidad media" value={mediaVelocidad !== null ? `${mediaVelocidad.toFixed(2)} hr/m\u00b2` : '--'} color={C.violet} sub={totalSuperficie > 0 && totalHoras > 0 ? `${fmtHoras(totalHoras)} h \u00f7 ${fmt(totalSuperficie)} m\u00b2` : 'Sin datos'} />
      </div>

      {/* KPIs fila 2 — económicos */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <KPICard label="Volumen" value={fmtEur(kpiVolumen)} color={C.positive}
          sub={proyFiltrados.some(p => p.esEstimado) ? 'Incluye estimaciones (est.)' : undefined} />
        <KPICard label="Resultado" value={fmtEur(kpiResultado)}
          color={kpiResultado >= 0 ? C.positive : C.negative}
          sub={!hayTarifas ? '⚠ Parcial: sin coste personal hasta Configuración' : proyFiltrados.some(p => p.esEstimado) ? 'Incluye estimaciones (est.)' : undefined} />
        <KPICard label="Margen medio" value={kpiMargen !== null ? fmtPct(kpiMargen) : '--'}
          color={kpiMargen !== null ? colorMargen(kpiMargen) : C.muted}
          sub="Resultado total / volumen total (ponderado)" />
      </div>

      {/* KPIs fila 3 — referencia empresa */}
      {kpisEmpresa && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <KPICard small
            label="Coste/hr empresa"
            value={fmtEur2(kpisEmpresa.costeHrEmpresa)}
            color={C.negative}
            sub={kpisEmpresa.usaEstructuraAnterior
              ? `⚠ Nóminas ${kpisEmpresa.anioNominas} + estructura estimada ${kpisEmpresa.anioEstructura}`
              : `Nóminas ${kpisEmpresa.anioNominas} + estructura ${kpisEmpresa.anioEstructura}`}
          />
          <KPICard small
            label="Venta/hr empresa"
            value={kpisEmpresa.ventaHrEmpresa !== null ? fmtEur2(kpisEmpresa.ventaHrEmpresa) : '--'}
            color={kpisEmpresa.ventaHrEmpresa !== null && kpisEmpresa.ventaHrEmpresa > kpisEmpresa.costeHrEmpresa ? C.positive : C.warning}
            sub={`Volumen ${kpisEmpresa.anioNominas} ÷ horas disponibles`}
          />
        </div>
      )}

      {/* Tabla detallada */}
      <SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span>Tabla detallada — {proyOrdenados.length} proyectos</span>
          <button onClick={() => setMostrarConfig(v => !v)} style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: mostrarConfig ? C.indigo : 'transparent', color: C.text, fontSize: 12, cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal' }}>
            ⚙ Columnas
          </button>
        </div>
      </SectionTitle>

      {mostrarConfig && (
        <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {COLUMNAS_TABLA.map(col => (
            <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: colsActivas.has(col.key) ? C.text : C.muted }}>
              <input type="checkbox" checked={colsActivas.has(col.key)} onChange={() => toggleColumna(col.key)} style={{ accentColor: C.indigo }} />
              {col.label}
            </label>
          ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${C.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.surface2 }}>
              {COLUMNAS_TABLA.filter(c => colsActivas.has(c.key)).map(col => (
                <th key={col.key} onClick={() => toggleOrden(col.key)} style={{ padding: '11px 14px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, userSelect: 'none', background: ordenCol === col.key ? 'rgba(83,74,183,0.15)' : 'transparent' }}>
                  {col.label} {iconOrden(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {proyOrdenados.length === 0 ? (
              <tr><td colSpan={99} style={{ padding: 32, textAlign: 'center', color: C.muted }}>Sin proyectos con los filtros actuales</td></tr>
            ) : proyOrdenados.map((p, idx) => {
              const margenColor = colorMargen(p.margen);
              return (
                <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? 'transparent' : 'rgba(83,74,183,0.04)', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(83,74,183,0.04)'}>
                  {COLUMNAS_TABLA.filter(c => colsActivas.has(c.key)).map(col => {
                    let cell = null;
                    switch (col.key) {
                      case 'codigo':      cell = <span style={{ fontFamily: 'monospace', color: C.violet, fontWeight: 600 }}>{p.codigo || '--'}</span>; break;
                      case 'nombre':      cell = <span style={{ color: C.text, fontWeight: 500 }}>{p.nombre}</span>; break;
                      case 'cliente':     cell = p.cliente; break;
                      case 'sector':      cell = p.sector || '--'; break;
                      case 'año':         cell = p.año || '--'; break;
                      case 'superficie':  cell = p.superficie ? `${fmt(p.superficie)} m²` : '--'; break;
                      case 'encargo':     cell = p.importe_encargo ? fmtEur(p.importe_encargo) : '--'; break;
                      case 'facturado':   cell = <span>{fmtEur(p.base)}{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span>; break;
                      case 'horas':       cell = p.horas > 0 ? `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.horas)} h` : '--'; break;
                      case 'costePersonal': cell = p.costePersonal !== null ? <span style={{ color: C.negative }}>{fmtEur(p.costePersonal)}{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : <span style={{ color: C.muted }}>--</span>; break;
                      case 'costeProd':   cell = p.costeProd > 0 ? fmtEur(p.costeProd) : '--'; break;
                      case 'ventaM2':     cell = p.ventaM2 !== null ? <span>{fmtEur2(p.ventaM2)}<span style={{ fontSize: 10, color: C.muted }}>/m²</span>{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : '--'; break;
                      case 'costeM2':     cell = p.costeM2 !== null ? <span>{fmtEur2(p.costeM2)}<span style={{ fontSize: 10, color: C.muted }}>/m²</span>{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : '--'; break;
                      case 'ventaHr':     cell = p.ventaHr !== null ? <span>{fmtEur2(p.ventaHr)}<span style={{ fontSize: 10, color: C.muted }}>/hr</span>{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : '--'; break;
                      case 'costeHr':     cell = p.costeHr !== null ? <span>{fmtEur2(p.costeHr)}<span style={{ fontSize: 10, color: C.muted }}>/hr</span>{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : '--'; break;
                      case 'resultado':   cell = <span style={{ color: p.resultado >= 0 ? C.positive : C.negative, fontWeight: 600 }}>{fmtEur(p.resultado)}{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span>; break;
                      case 'margen':      cell = p.margen !== null ? <span style={{ color: margenColor, fontWeight: 700 }}>{fmtPct(p.margen)}{p.esEstimado && <span style={{ fontSize: 10, color: C.warning, marginLeft: 4 }}>est.</span>}</span> : '--'; break;
                      case 'velocidad':   cell = p.velocidad !== null ? `${p.velocidad} hr/m²` : '--'; break;
                      default:            cell = '--';
                    }
                    return <td key={col.key} style={{ padding: '10px 14px', color: C.muted, whiteSpace: 'nowrap' }}>{cell}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
          {proyOrdenados.length > 0 && (
            <tfoot>
              <tr style={{ background: C.surface2, borderTop: `2px solid ${C.border}` }}>
                {COLUMNAS_TABLA.filter(c => colsActivas.has(c.key)).map(col => {
                  let cell = '';
                  switch (col.key) {
                    case 'codigo':    cell = <span style={{ fontWeight: 700, color: C.text }}>TOTALES</span>; break;
                    case 'horas':     { const t = proyOrdenados.reduce((s, p) => s + (p.horas || 0), 0); cell = t > 0 ? <span style={{ fontWeight: 700, color: C.text }}>{t.toFixed(1)} h</span> : '--'; } break;
                    case 'facturado': cell = <span style={{ fontWeight: 700, color: C.positive }}>{fmtEur(kpiVolumen)}</span>; break;
                    case 'costePersonal': { const t = proyOrdenados.reduce((s, p) => s + (p.costePersonal || 0), 0); cell = hayTarifas ? <span style={{ fontWeight: 700, color: C.negative }}>{fmtEur(t)}</span> : '--'; } break;
                    case 'resultado': cell = <span style={{ fontWeight: 700, color: kpiResultado >= 0 ? C.positive : C.negative }}>{fmtEur(kpiResultado)}</span>; break;
                    case 'margen':    cell = kpiMargen !== null ? <span style={{ fontWeight: 700, color: colorMargen(kpiMargen) }}>{fmtPct(kpiMargen)}</span> : '--'; break;
                    default: cell = '';
                  }
                  return <td key={col.key} style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: C.muted }}>{cell}</td>;
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// BOTÓN SCROLL-TO-TOP
// ─────────────────────────────────────────────
const ScrollTopBtn = ({ refContenedor }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = refContenedor?.current;
    if (!el) return;
    const onScroll = () => setVisible(el.scrollTop > 300);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [refContenedor]);

  if (!visible) return null;

  return (
    <button
      onClick={() => refContenedor?.current?.scrollTo({ top: 0, behavior: 'smooth' })}
      title="Volver arriba"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 100,
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: C.indigo,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(83,74,183,0.5)',
        color: '#fff',
        fontSize: 18,
        transition: 'opacity 0.2s, transform 0.2s',
        opacity: 0.9,
      }}
      onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'scale(1)'; }}
    >
      ↑
    </button>
  );
};

// ─────────────────────────────────────────────
// PESTAÑA PRODUCTIVIDAD
// ─────────────────────────────────────────────
const CATEGORIA_COLORES = [
  '#534AB7','#7B6FE8','#1D9E75','#D85A30','#BA7517',
  '#3B82F6','#EC4899','#10B981','#F59E0B','#6366F1','#A78BFA','#34D399',
];

function PestanaProductividad({ usuarios, proyectos, tarifasMap }) {
  // Sesión 33: los años van del primer año con horas (1 lectura) al actual; solo se leen los años marcados.
  const primerAnio = usePrimerAnioHoras();
  const añosDisponibles = useMemo(() => {
    const actual = new Date().getFullYear();
    const desde = primerAnio ? Math.min(parseInt(primerAnio, 10), actual) : actual;
    const lista = [];
    for (let y = actual; y >= desde; y--) lista.push(String(y));
    return lista;
  }, [primerAnio]);

  const [añosActivos, setAñosActivos] = useState(() => [String(new Date().getFullYear())]);
  const [empleadoFiltro, setEmpleadoFiltro] = useState('');
  const horas = useHoras({ anios: añosActivos });
  const registrosHoras = horas.registros;

  // Mapa de id → nombre real desde colección usuarios
  const nombresMap = useMemo(() => {
    const map = {};
    usuarios.forEach(u => { map[u.id] = u.nombre || u.email || u.id; });
    return map;
  }, [usuarios]);

  const toggleAño = (y) => {
    setAñosActivos(prev =>
      prev.includes(y) ? (prev.length > 1 ? prev.filter(a => a !== y) : prev) : [...prev, y]
    );
  };

  // Empleados únicos en registros — nombre desde usuarios reales
  const empleadosDisponibles = useMemo(() => {
    const ids = new Set();
    registrosHoras.forEach(r => { if (r.empleadoId) ids.add(r.empleadoId); });
    return [...ids]
      .map(id => ({ id, nombre: nombresMap[id] || id }))
      .sort((a,b) => a.nombre.localeCompare(b.nombre,'es'));
  }, [registrosHoras, nombresMap]);

  // Registros filtrados por año y empleado
  const registrosFiltrados = useMemo(() => {
    return registrosHoras.filter(r => {
      if (!r.fecha) return false;
      if (!añosActivos.includes(r.fecha.slice(0,4))) return false;
      if (empleadoFiltro && r.empleadoId !== empleadoFiltro) return false;
      return true;
    });
  }, [registrosHoras, añosActivos, empleadoFiltro]);

  // Horas totales
  const horasTotales    = registrosFiltrados.reduce((s,r) => s + (parseFloat(r.horas)||0), 0);
  const horasProyecto   = registrosFiltrados.filter(r => !r.esInterno).reduce((s,r) => s + (parseFloat(r.horas)||0), 0);
  const horasInternas   = registrosFiltrados.filter(r => r.esInterno).reduce((s,r) => s + (parseFloat(r.horas)||0), 0);
  const pctProyecto     = horasTotales > 0 ? (horasProyecto / horasTotales * 100) : 0;

  const esActivo = (u) => u.estado === 'activo' || u.activo === true || (!u.estado && u.activo !== false);

  // Horas esperadas: usa tarifas reales si están cargadas, fallback 1712 si no
  const horasEsperadas = useMemo(() => {
    const esActivo = (u) => u.estado === 'activo' || u.activo === true || (!u.estado && u.activo !== false);
    const usuariosActivos = usuarios.filter(esActivo);
    if (!usuariosActivos.length) return 0;
    if (empleadoFiltro) {
      const entrada = tarifasMap[empleadoFiltro];
      return entrada?.horas_anuales || 0;
    }
    return usuariosActivos.reduce((s, u) => {
      const entrada = tarifasMap[u.id];
      return s + (entrada?.horas_anuales || 0); // sin fallback — solo usuarios con tarifa real
    }, 0);
  }, [usuarios, tarifasMap, añosActivos, empleadoFiltro]);

  const horasGap = Math.max(0, horasEsperadas - horasTotales);

  // Distribución por categoría (horas internas)
  const distribucionCategorias = useMemo(() => {
    const map = {};
    registrosFiltrados.filter(r => r.esInterno).forEach(r => {
      const cat = r.categoria || 'Sin categoría';
      map[cat] = (map[cat] || 0) + (parseFloat(r.horas)||0);
    });
    return Object.entries(map)
      .map(([cat, horas]) => ({ cat, horas, pct: horasInternas > 0 ? (horas/horasInternas*100) : 0 }))
      .sort((a,b) => b.horas - a.horas);
  }, [registrosFiltrados, horasInternas]);

  // Evolución mensual: proyectos vs internas
  const evolucionMensual = useMemo(() => {
    return MESES.map((mes, i) => {
      const hP = registrosFiltrados.filter(r => !r.esInterno && r.fecha && parseInt(r.fecha.slice(5,7))-1 === i)
        .reduce((s,r) => s+(parseFloat(r.horas)||0), 0);
      const hI = registrosFiltrados.filter(r => r.esInterno && r.fecha && parseInt(r.fecha.slice(5,7))-1 === i)
        .reduce((s,r) => s+(parseFloat(r.horas)||0), 0);
      return { mes, proyectos: +hP.toFixed(1), internas: +hI.toFixed(1) };
    });
  }, [registrosFiltrados]);

  // Horas por empleado (solo si no hay filtro de empleado)
  const horasPorEmpleado = useMemo(() => {
    if (empleadoFiltro) return [];
    const map = {};
    registrosFiltrados.forEach(r => {
      const id = r.empleadoId;
      if (!id) return;
      if (!map[id]) map[id] = { nombre: nombresMap[id] || r.empleadoNombre || id, proyecto: 0, interno: 0 };
      if (r.esInterno) map[id].interno += parseFloat(r.horas)||0;
      else map[id].proyecto += parseFloat(r.horas)||0;
    });
    return Object.values(map)
      .map(e => ({ ...e, total: e.proyecto + e.interno }))
      .sort((a,b) => b.total - a.total);
  }, [registrosFiltrados, empleadoFiltro, nombresMap]);

  // Datos para gráfico de distribución global (proyectos + categorías internas)
  const datosTarta = useMemo(() => {
    const items = [{ name: 'Proyectos', value: +horasProyecto.toFixed(1) }];
    distribucionCategorias.forEach(d => items.push({ name: d.cat, value: +d.horas.toFixed(1) }));
    return items.filter(i => i.value > 0);
  }, [horasProyecto, distribucionCategorias]);

  const S = {
    label: { color: C.mist, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', display:'block', marginBottom: 6 },
    select: { background: '#1a1440', border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 12px', color:'#fff', fontSize:13, outline:'none', colorScheme:'dark' },
  };

  if (horas.primeraCarga) return horas.error ? <ErrorHoras texto={horas.error} /> : <CargandoHoras />;

  return (
    <div>
      <AvisoHoras horas={horas} />
      {/* Filtros */}
      <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap', marginBottom:24 }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {añosDisponibles.length === 0
            ? <span style={{ color: C.muted, fontSize:13 }}>Sin datos</span>
            : añosDisponibles.map(y => (
                <button key={y} onClick={() => toggleAño(y)} style={{
                  padding:'5px 14px', borderRadius:20, fontSize:13, cursor:'pointer',
                  border:`1px solid ${añosActivos.includes(y) ? C.indigo : C.border}`,
                  background: añosActivos.includes(y) ? C.indigo : 'transparent',
                  color: añosActivos.includes(y) ? '#fff' : C.muted,
                  fontWeight: añosActivos.includes(y) ? 600 : 400,
                  transition:'all 0.15s',
                }}>
                  {y}
                </button>
              ))
          }
        </div>
        <select value={empleadoFiltro} onChange={e => setEmpleadoFiltro(e.target.value)} style={S.select}>
          <option value=''>Equipo completo</option>
          {empleadosDisponibles.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
      </div>

      {/* KPIs fila 1 */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:12 }}>
        <KPICard label="Horas totales registradas" value={`${fmtHoras(horasTotales)} h`} />
        <KPICard label="En proyectos" value={`${fmtHoras(horasProyecto)} h`} sub={`${pctProyecto.toFixed(1)}% del total`} color={C.positive} />
        <KPICard label="Horas internas" value={`${fmtHoras(horasInternas)} h`} sub={`${(100-pctProyecto).toFixed(1)}% del total`} color={C.violet} />
        <KPICard label="Dedicación a proyectos" value={`${pctProyecto.toFixed(1)}%`} color={pctProyecto >= 70 ? C.positive : pctProyecto >= 50 ? C.warning : C.negative} />
      </div>

      {/* KPIs fila 2 */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:28 }}>
        <KPICard
          label="Horas esperadas"
          value={horasEsperadas > 0 ? `${fmtHoras(horasEsperadas)} h` : '—'}
          sub={horasEsperadas > 0
            ? 'Basado en horas anuales de tarifas configuradas'
            : 'Configura tarifas en Configuración → Usuarios'}
          small
        />
        <KPICard
          label="Horas no registradas (gap)"
          value={horasEsperadas > 0 ? (horasGap > 0 ? `${fmtHoras(horasGap)} h` : '—') : '—'}
          sub={horasEsperadas > 0
            ? (horasGap > 0 ? 'Horas sin contabilizar' : 'Sin gap detectado')
            : 'Requiere tarifas configuradas'}
          color={horasGap > 0 && horasEsperadas > 0 ? C.warning : undefined}
          small
        />
        <KPICard
          label="Tasa de utilización"
          value={horasEsperadas > 0 ? `${(horasProyecto / horasEsperadas * 100).toFixed(1)}%` : '—'}
          color={(() => { if (!horasEsperadas) return C.muted; const t = horasProyecto / horasEsperadas * 100; return t >= 75 ? C.positive : t >= 60 ? C.warning : C.negative; })()}
          sub="Horas proyecto ÷ horas disponibles. Ref: 60–65% estudio / 75–85% técnico"
          small
        />
      </div>

      {/* Gráficos */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>

        {/* Tarta distribución */}
        <ChartCard title="Distribución de horas" height={260}>
          {datosTarta.length === 0 ? <SinDatos /> : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={datosTarta} cx="40%" cy="50%" outerRadius={100} dataKey="value" nameKey="name">
                  {datosTarta.map((_, i) => <Cell key={i} fill={CATEGORIA_COLORES[i % CATEGORIA_COLORES.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `${v} h`} contentStyle={{ background:'rgba(14,11,36,0.97)', border:`1px solid ${C.border}`, borderRadius:10, fontSize:13 }} />
                <Legend
                  layout="vertical" align="right" verticalAlign="middle"
                  formatter={(v) => <span style={{ color:'#fff', fontSize:12 }}>{v}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Evolución mensual */}
        <ChartCard title="Evolución mensual" height={260}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={evolucionMensual} barSize={10}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="mes" tick={{ fill: C.muted, fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.muted, fontSize:11 }} axisLine={false} tickLine={false} unit="h" />
              <Tooltip content={<TooltipCustom formatValue={v => `${v} h`} />} />
              <Legend formatter={v => <span style={{ color:'#fff', fontSize:12 }}>{v}</span>} />
              <Bar dataKey="proyectos" name="Proyectos" fill={C.indigo} radius={[3,3,0,0]} />
              <Bar dataKey="internas"  name="Internas"  fill={C.violet} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Tabla categorías internas */}
      {distribucionCategorias.length > 0 && (
        <>
          <SectionTitle>Desglose horas internas por categoría</SectionTitle>
          <div style={{ background: C.surface2, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', marginBottom:24 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Categoría','Horas','% s/ internas','% s/ total'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', color:C.muted, fontSize:11, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:`1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {distribucionCategorias.map((d, i) => (
                  <tr key={d.cat} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'12px 16px', color:'#fff', fontSize:13 }}>
                      <span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:CATEGORIA_COLORES[(i+1)%CATEGORIA_COLORES.length], marginRight:8 }} />
                      {d.cat}
                    </td>
                    <td style={{ padding:'12px 16px', color:'#fff', fontWeight:600, fontSize:13 }}>{fmtHoras(d.horas)} h</td>
                    <td style={{ padding:'12px 16px', color:C.violet, fontSize:13 }}>{d.pct.toFixed(1)}%</td>
                    <td style={{ padding:'12px 16px', color:C.muted, fontSize:13 }}>{horasTotales > 0 ? (d.horas/horasTotales*100).toFixed(1) : '0.0'}%</td>
                  </tr>
                ))}
                <tr style={{ background:'rgba(83,74,183,0.06)' }}>
                  <td style={{ padding:'10px 16px', color:C.muted, fontSize:12, fontWeight:600 }}>TOTAL INTERNAS</td>
                  <td style={{ padding:'10px 16px', color:'#fff', fontWeight:700, fontSize:13 }}>{fmtHoras(horasInternas)} h</td>
                  <td style={{ padding:'10px 16px', color:C.muted, fontSize:13 }}>100%</td>
                  <td style={{ padding:'10px 16px', color:C.muted, fontSize:13 }}>{horasTotales > 0 ? (horasInternas/horasTotales*100).toFixed(1) : '0.0'}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Tabla por empleado */}
      {horasPorEmpleado.length > 0 && (
        <>
          <SectionTitle>Horas por empleado</SectionTitle>
          <div style={{ background: C.surface2, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Empleado','Proyectos','Internas','Total','% Proyectos'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', color:C.muted, fontSize:11, fontWeight:500, textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:`1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {horasPorEmpleado.map((e, i) => {
                  const pctP = e.total > 0 ? (e.proyecto / e.total * 100) : 0;
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                      <td style={{ padding:'12px 16px', color:'#fff', fontWeight:500, fontSize:13 }}>{e.nombre}</td>
                      <td style={{ padding:'12px 16px', color:C.positive, fontSize:13 }}>{fmtHoras(e.proyecto)} h</td>
                      <td style={{ padding:'12px 16px', color:C.violet, fontSize:13 }}>{fmtHoras(e.interno)} h</td>
                      <td style={{ padding:'12px 16px', color:'#fff', fontWeight:700, fontSize:13 }}>{fmtHoras(e.total)} h</td>
                      <td style={{ padding:'12px 16px', fontSize:13 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1, height:6, borderRadius:3, background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
                            <div style={{ width:`${pctP}%`, height:'100%', background:pctP >= 70 ? C.positive : pctP >= 50 ? C.warning : C.negative, borderRadius:3, transition:'width 0.3s' }} />
                          </div>
                          <span style={{ color: pctP >= 70 ? C.positive : pctP >= 50 ? C.warning : C.negative, fontWeight:600, minWidth:36 }}>{pctP.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {registrosFiltrados.length === 0 && (
        <div style={{ textAlign:'center', padding:'60px 24px', color:C.muted, fontSize:14 }}>
          Sin registros de horas para los filtros seleccionados.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL — ANALISIS
// ─────────────────────────────────────────────
export default function Analisis({ refContenedor, esAdmin, soloCRM }) {
  // soloCRM=true: el comercial solo ve la pestaña CRM, nunca Rendimiento
  const [pestana, setPestana] = useState('crm');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [oportunidades, setOportunidades] = useState([]);
  const [leads, setLeads] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [tarifasMap, setTarifasMap] = useState({});
  const [kpisEmpresa, setKpisEmpresa] = useState(null);
  // kpisEmpresa: { costeHrEmpresa, ventaHrEmpresa, anioNominas, anioEstructura, usaEstructuraAnterior, nominasActual, gastosEstructura, horasDisponiblesAnio }

  // Sesión 32 · Entrega B: las colecciones principales vienen del almacén compartido (si otro módulo ya las
  // descargó en esta sesión y siguen vigentes, no cuestan lecturas). Usuarios y tarifas se leen aparte.
  const obtener = useObtener();

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setCargando(true);
        // Cargamos colecciones principales en paralelo
        // Sesión 31: las horas NO se cargan aquí. Se cargan solo al abrir una pestaña que las usa
        // (Empresa, Proyectos, Productividad). Quien solo mira el CRM no las descarga nunca.
        const [leadsAlm, presAlm, proyAlm, clientesAlm, opsAlm, snUsuarios] = await Promise.all([
          obtener('leads'),
          obtener('presupuestos'),
          obtener('proyectos'),
          obtener('clientes'),
          obtener('oportunidades'),
          // Sesión 33: el comercial (soloCRM) no puede leer usuarios ni tarifas: solo ve la pestaña CRM
          soloCRM ? Promise.resolve({ docs: [] }) : getDocs(collection(db, 'usuarios')),
        ]);

        const leadsMap = {};
        leadsAlm.forEach(l => { leadsMap[l.id] = l; });
        setLeads(leadsAlm);
        setOportunidades(opsAlm.map(o => ({ fuente: leadsMap[o.leadId]?.fuente || null, ...o })));
        setPresupuestos(presAlm);

        const proyectosData = proyAlm;
        setProyectos(proyectosData);
        setClientes(clientesAlm);
        if (soloCRM) return; // usuarios, tarifas y KPIs de empresa son solo para el administrador


        const esActivo = (u) => u.estado === 'activo' || u.activo === true || (!u.estado && u.activo !== false);
        const usuariosData = snUsuarios.docs.map(d => ({ id: d.id, ...d.data() }));
        setUsuarios(usuariosData);

        // Cargar tarifas de TODOS los usuarios para cálculo de coste personal
        const tMap = {};
        await Promise.all(
          usuariosData.map(async u => {
            try {
              const snap = await getDocs(collection(db, 'usuarios', u.id, 'tarifas'));
              if (snap.empty) { tMap[u.id] = { horas_anuales: 0, tarifas: [] }; return; }
              const todas = snap.docs.map(d => d.data()).sort((a, b) => (b.fecha_inicio || '').localeCompare(a.fecha_inicio || ''));
              tMap[u.id] = { horas_anuales: todas[0]?.horas_anuales || 1712, tarifas: todas };
            } catch { tMap[u.id] = { horas_anuales: 0, tarifas: [] }; }
          })
        );
        setTarifasMap(tMap);

        // ── KPIs empresa — calcular una vez aquí, pasar como prop ──
        const anioActual = new Date().getFullYear();

        // Nóminas y horas: SIEMPRE del año actual (tarifa vigente hoy)
        let nominasActual = 0;
        let horasDisponiblesActual = 0;
        const fechaHoy = new Date().toISOString().slice(0, 10);
        usuariosData.filter(esActivo).forEach(u => {
          const entrada = tMap[u.id];
          if (!entrada?.tarifas?.length) return;
          const tarifa = entrada.tarifas.find(t => {
            const desde = t.fecha_inicio || '';
            const hasta = t.fecha_fin || '9999-12-31';
            return fechaHoy >= desde && fechaHoy < hasta;
          }) || entrada.tarifas[0]; // fallback: más reciente
          if (!tarifa) return;
          nominasActual += tarifa.coste_empresa || 0;
          horasDisponiblesActual += tarifa.horas_anuales || 0;
        });

        // Estructura: año actual si existe, si no año anterior como estimación
        let gastosEstructura = 0;
        let anioEstructura = anioActual;
        let usaEstructuraAnterior = false;
        try {
          const snapEst = await getDoc(doc(db, 'config', 'estructura_costes', 'anios', String(anioActual)));
          if (snapEst.exists() && snapEst.data().gastos > 0) {
            gastosEstructura = snapEst.data().gastos;
          } else {
            const snapAnt = await getDoc(doc(db, 'config', 'estructura_costes', 'anios', String(anioActual - 1)));
            if (snapAnt.exists()) {
              gastosEstructura = snapAnt.data().gastos || 0;
              anioEstructura = anioActual - 1;
              usaEstructuraAnterior = true;
            }
          }
        } catch { /* sin estructura */ }

        // Volumen año actual
        const volumenActual = proyectosData.reduce((s, p) => {
          const añoProy = getYear(p.fecha_inicio || p.creado_en);
          if (añoProy !== anioActual) return s;
          return s + (p.estado === 'finalizado' ? (p.importe_facturado || p.importe_encargo || 0) : (p.importe_encargo || 0));
        }, 0);

        const kpis = horasDisponiblesActual > 0 ? {
          costeHrEmpresa: (nominasActual + gastosEstructura) / horasDisponiblesActual,
          ventaHrEmpresa: volumenActual > 0 ? volumenActual / horasDisponiblesActual : null,
          anioNominas: anioActual,
          anioEstructura,
          usaEstructuraAnterior,
          nominasActual,
          gastosEstructura,
          horasDisponiblesAnio: horasDisponiblesActual,
        } : null;
        setKpisEmpresa(kpis);

      } catch (e) {
        console.error('Error cargando datos de análisis:', e);
        setError('No se pudieron cargar los datos. Revisa la consola del navegador (F12).');
      } finally {
        setCargando(false);
      }
    };
    cargarDatos();
  }, [obtener]);

  // Sesión 33: las horas ya no se cargan aquí. Cada pestaña (Empresa, Proyectos, Productividad) pide con useHoras
  // solo los trozos que necesita (años elegidos o proyectos visibles), compartidos en memoria durante la sesión.

  const estilosPestana = (activa) => ({
    padding: '9px 22px',
    borderRadius: '8px 8px 0 0',
    border: `1px solid ${activa ? C.border : 'transparent'}`,
    borderBottom: activa ? `1px solid ${C.bg}` : 'none',
    background: activa ? C.bg : 'transparent',
    color: activa ? C.text : C.muted,
    fontSize: 14,
    fontWeight: activa ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
    marginBottom: -1,
    position: 'relative',
  });

  if (cargando) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12, color: C.muted }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${C.indigo}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
      Cargando datos de análisis…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: 32, color: C.negative, background: C.surface2, borderRadius: 12, margin: 24 }}>{error}</div>
  );

  return (
    <div style={{ padding: '0', maxWidth: 1400, margin: '0 auto' }}>

      {/* ── CABECERA FIJA — título + pestañas ── */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
        padding: isMobile ? '12px 16px 0' : '18px 28px 0',
        backdropFilter: 'blur(8px)',
      }}>
        <div style={{ marginBottom: 14 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Análisis</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: '3px 0 0' }}>
            Datos en tiempo real · {oportunidades.length} oportunidades · {presupuestos.length} presupuestos · {proyectos.length} proyectos
          </p>
        </div>
        <div style={{ display: 'flex', overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
          <button style={estilosPestana(pestana === 'crm')} onClick={() => setPestana('crm')}>
            CRM / Oportunidades
          </button>
          {/* Rendimiento y Productividad: solo admin */}
          {!soloCRM && (
            <button style={estilosPestana(pestana === 'rent')} onClick={() => setPestana('rent')}>
              Empresa
            </button>
          )}
          {!soloCRM && (
            <button style={estilosPestana(pestana === 'proyectos')} onClick={() => setPestana('proyectos')}>
              Proyectos
            </button>
          )}
          {!soloCRM && (
            <button style={estilosPestana(pestana === 'prod')} onClick={() => setPestana('prod')}>
              Productividad
            </button>
          )}
        </div>
      </div>

      {/* ── CONTENIDO con scroll ── */}
      <div style={{ padding: isMobile ? '16px' : '24px 28px' }}>
        {pestana === 'crm' && (
          <PestanaCRM oportunidades={oportunidades} presupuestos={presupuestos} proyectos={proyectos} leads={leads} />
        )}
        {pestana === 'rent' && !soloCRM && (
          <PestanaRentabilidades proyectos={proyectos} clientes={clientes} tarifasMap={tarifasMap} kpisEmpresa={kpisEmpresa} />
        )}
        {pestana === 'proyectos' && !soloCRM && (
          <PestanaProyectos proyectos={proyectos} clientes={clientes} tarifasMap={tarifasMap} kpisEmpresa={kpisEmpresa} />
        )}
        {pestana === 'prod' && !soloCRM && (
          <PestanaProductividad usuarios={usuarios} proyectos={proyectos} tarifasMap={tarifasMap} />
        )}
      </div>

      {/* ── BOTÓN SCROLL-TO-TOP flotante ── */}
      <ScrollTopBtn refContenedor={refContenedor} />
    </div>
  );
}
